// StudyGroupCard — dashboard widget: create a group, share the link, see who's in.
// Members get 14 days of Pro on first join (one-time per user, no stacking).
// Also renders a chat wall of recent quiz-grade reactions so the group feels alive.
import { useCallback, useEffect, useState } from "react";
import { Users, Copy, Check, Loader2, Plus, Share2, Sparkles, MessageCircle, Star, Send } from "lucide-react";
import { createStudyGroup, listMyStudyGroups, listStudyGroupReactions, postStudyGroupReaction } from "@/lib/api";
import { toast } from "sonner";

const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const ReactionRow = ({ r }) => (
  <div
    className={`rounded-xl border ${r.is_me ? "border-accent/40 bg-accent/5" : "border-border bg-background"} p-3`}
    data-testid={`group-reaction-${r.id}`}
  >
    <div className="flex items-start gap-2">
      <span className="grid place-items-center h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
        {(r.display_name || "?").slice(0, 2).toUpperCase()}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="text-sm font-bold">{r.display_name}{r.is_me && <span className="ml-1 text-[10px] text-accent uppercase tracking-widest">you</span>}</p>
          <span className="text-[10px] text-muted-foreground">{relTime(r.created_at)}</span>
        </div>
        {r.grade_letter && (
          <p className="mt-0.5 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-bold">
              <Star className="h-3 w-3 fill-current" /> {r.grade_letter}{r.pct != null && ` · ${r.pct}%`}
            </span>
            {r.lecture_title && <span className="ml-2 text-muted-foreground">on <span className="font-medium text-foreground">{r.lecture_title}</span></span>}
          </p>
        )}
        {r.message && <p className="mt-1 text-sm leading-snug">{r.message}</p>}
      </div>
    </div>
  </div>
);

const GroupRow = ({ g, onCopy, copiedCode }) => {
  const link = `${window.location.origin}${g.share_path}`;
  const [expanded, setExpanded] = useState(false);
  const [reactions, setReactions] = useState(null);
  const [msg, setMsg] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try { const r = await listStudyGroupReactions(g.code); setReactions(r.reactions || []); }
    catch { setReactions([]); }
  }, [g.code]);

  useEffect(() => { if (expanded && reactions === null) load(); }, [expanded, reactions, load]);

  const post = async (e) => {
    e.preventDefault();
    if (!msg.trim() || posting) return;
    setPosting(true);
    try {
      await postStudyGroupReaction(g.code, { message: msg.trim() });
      setMsg("");
      await load();
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Couldn't post.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-border bg-background p-4"
      data-testid={`study-group-row-${g.code}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-serif text-base font-bold truncate">{g.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{g.member_count}</span> / {g.max_members} seats
            {" · "}{g.seats_left > 0 ? `${g.seats_left} left` : "full"}
            {g.is_creator && <span className="ml-2 rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Creator</span>}
          </p>
        </div>
        <button
          data-testid={`study-group-copy-${g.code}`}
          onClick={() => onCopy(g.code, link)}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary/60 shrink-0"
        >
          {copiedCode === g.code ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Share2 className="h-3.5 w-3.5" /> Share link</>}
        </button>
      </div>
      <div className="mt-2 rounded-lg border border-input bg-secondary/40 px-3 py-2 font-mono text-[11px] truncate select-all" data-testid={`study-group-link-${g.code}`}>
        {link}
      </div>

      <button
        data-testid={`study-group-toggle-wall-${g.code}`}
        onClick={() => setExpanded((x) => !x)}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-secondary/60"
      >
        <MessageCircle className="h-3.5 w-3.5" />
        {expanded ? "Hide chat wall" : "Show chat wall"}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3" data-testid={`study-group-wall-${g.code}`}>
          <form onSubmit={post} className="flex gap-2">
            <input
              data-testid={`study-group-wall-msg-${g.code}`}
              value={msg} onChange={(e) => setMsg(e.target.value)}
              placeholder="Post a hype line, a question, or a grade brag…"
              maxLength={280}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
            <button
              type="submit"
              data-testid={`study-group-wall-send-${g.code}`}
              disabled={!msg.trim() || posting}
              className="inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-3 text-sm font-bold hover:opacity-90 disabled:opacity-50"
            >
              {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </button>
          </form>
          {reactions === null ? (
            <div className="grid place-items-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : reactions.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-3">
              No posts yet. Post an A grade to fire up the wall.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {reactions.map((r) => <ReactionRow key={r.id} r={r} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const StudyGroupCard = () => {
  const [groups, setGroups] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    try { const r = await listMyStudyGroups(); setGroups(r.groups || []); } catch (e) { setErr(e); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (err || groups === null) {
    return null; // fail silently — dashboard already has plenty going on
  }

  const create = async (e) => {
    e.preventDefault();
    if (creating || name.trim().length < 3) return;
    setCreating(true);
    try {
      const res = await createStudyGroup(name.trim());
      toast.success(`Group "${res.name}" created. Share the link with up to ${res.max_members - 1} friends.`);
      setName("");
      await refresh();
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Couldn't create the group.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(code);
      toast.success("Study-group link copied — paste it in your group chat");
      setTimeout(() => setCopied(null), 2200);
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <div data-testid="study-group-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <Users className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Study group invite</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Name a group, share one link, and up to 8 friends join with <span className="font-semibold text-foreground">14 days of Pro</span> each — one link, one login each, no card required.
          </p>
        </div>
      </div>

      <form onSubmit={create} className="mt-5 grid grid-cols-[1fr_auto] gap-2" data-testid="study-group-create-form">
        <input
          data-testid="study-group-name"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder='e.g. "AP Bio squad" or "Honors Chem study crew"'
          maxLength={80}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
        />
        <button
          data-testid="study-group-create-btn"
          type="submit"
          disabled={creating || name.trim().length < 3}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary text-primary-foreground px-4 text-sm font-bold hover:opacity-90 disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create
        </button>
      </form>

      {groups.length > 0 ? (
        <div className="mt-5 space-y-3">
          {groups.map((g) => (
            <GroupRow key={g.code} g={g} onCopy={copy} copiedCode={copied} />
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          Free tip: name it after your class ("AP Bio 2027") so friends recognize it in a group chat.
        </p>
      )}
    </div>
  );
};

export default StudyGroupCard;
